/**
 * プレミアム購入フロー
 * - iOS Capacitor: StoreKit（@capgo/native-purchases）
 * - Web / Android: Stripe Checkout
 *
 * 注: @capacitor-community/in-app-purchases は npm 未公開のため、
 * StoreKit 直接ラップの @capgo/native-purchases を使用しています。
 */
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { NativePurchases, PURCHASE_TYPE } from '@capgo/native-purchases';

/** App Store Connect で登録する Product ID */
export const IAP_PREMIUM_PRODUCT_ID = 'com.shotaezaki.jtalk.premium';

/**
 * iOS Capacitor ネイティブか（Web / Android は false）
 */
export function isIosNativeCheckout() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

/** プレミアム購入ボタンのラベル */
export function getPremiumPurchaseButtonLabel() {
  return isIosNativeCheckout() ? 'App Storeで購入' : 'クレジットカードで支払う';
}

/**
 * Stripe Checkout へ遷移（Web / Capacitor WebView）
 */
export function openCheckoutUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    window.location.assign(url);
    return true;
  } catch {
    try {
      const opened = window.open(url, '_self');
      return Boolean(opened);
    } catch {
      return false;
    }
  }
}

function isUserCancelledError(err) {
  const code = String(err?.code || err?.errorCode || '').toLowerCase();
  const msg = String(err?.message || err?.errorMessage || '').toLowerCase();
  return (
    code.includes('cancel')
    || /cancel|cancelled|canceled|user cancel|paymentcancelled|skerror/.test(msg)
  );
}

/** 購入完了後: サーバーでプレミアム有効化 */
async function syncPremiumAfterIap(transactionId, method = 'ios_iap') {
  await axios.post('/api/users/me/talk-premium/activate-iap', {
    method,
    transactionId,
  });
}

let billingSupportedCache = null;

async function ensureStoreKitAvailable() {
  if (billingSupportedCache !== null) return billingSupportedCache;
  try {
    const { isBillingSupported } = await NativePurchases.isBillingSupported();
    billingSupportedCache = Boolean(isBillingSupported);
  } catch {
    billingSupportedCache = false;
  }
  return billingSupportedCache;
}

let iapProductCache = null;
let iapProductPromise = null;

/** App Store から商品情報を取得（キャッシュ済みなら即返却） */
export async function loadIapPremiumProduct() {
  if (!isIosNativeCheckout()) return null;
  if (iapProductCache) return iapProductCache;

  if (!iapProductPromise) {
    iapProductPromise = (async () => {
      const ok = await ensureStoreKitAvailable();
      if (!ok) throw new Error('この端末ではアプリ内課金が利用できません');
      const { product } = await NativePurchases.getProduct({
        productIdentifier: IAP_PREMIUM_PRODUCT_ID,
        productType: PURCHASE_TYPE.SUBS,
      });
      if (!product) throw new Error('App Store の商品情報を取得できませんでした');
      iapProductCache = product;
      return product;
    })();
  }

  try {
    return await iapProductPromise;
  } catch (e) {
    iapProductPromise = null;
    throw new Error(e?.message || 'App Store の商品情報を取得できませんでした');
  }
}

/** アプリ起動時にバックグラウンドで商品情報を事前取得する */
export function prefetchIapProduct() {
  if (!isIosNativeCheckout()) return;
  loadIapPremiumProduct().catch(() => {});
}

/**
 * iOS: StoreKit 購入 → purchase-demo → 呼び出し元で refreshMe
 */
export async function purchasePremiumViaIap() {
  if (!isIosNativeCheckout()) {
    throw new Error('App Store 購入は iOS アプリでのみ利用できます');
  }

  const ok = await ensureStoreKitAvailable();
  if (!ok) {
    throw new Error('この端末ではアプリ内課金が利用できません');
  }

  let transaction;
  try {
    transaction = await NativePurchases.purchaseProduct({
      productIdentifier: IAP_PREMIUM_PRODUCT_ID,
      productType: PURCHASE_TYPE.SUBS,
      quantity: 1,
    });
  } catch (e) {
    if (isUserCancelledError(e)) {
      const err = new Error('購入がキャンセルされました');
      err.cancelled = true;
      throw err;
    }
    throw new Error(e?.message || e?.errorMessage || 'App Store での購入に失敗しました');
  }

  if (!transaction?.transactionId) {
    throw new Error('購入情報を取得できませんでした');
  }

  try {
    await syncPremiumAfterIap(transaction.transactionId);
  } catch (e) {
    throw new Error(
      e?.response?.data?.error || '購入は完了しましたが、サーバーへの反映に失敗しました'
    );
  }

  return { method: 'ios_iap', transactionId: transaction.transactionId };
}

/**
 * 復元結果として有効とみなせる購入だけを残す
 * （返金・失効済みのトランザクションでプレミアムを付与しないため）
 */
function isLiveEntitlement(p) {
  if (!p) return false;
  if (p.revocationDate) return false;
  if (p.isActive === false) return false;
  if (p.expirationDate) {
    const exp = Date.parse(p.expirationDate);
    if (Number.isFinite(exp) && exp <= Date.now()) return false;
  }
  return true;
}

/**
 * 現在サインイン中の Apple ID が保有している有効な購入を取得する。
 * restorePurchases() は StoreKit の AppStore.sync() を呼ぶだけで戻り値を返さないため、
 * 復元の成否はこちらで読み直す必要がある。
 */
async function fetchActiveIapEntitlements() {
  // 第一候補: Transaction.currentEntitlements（サインイン中の Apple ID の有効な権利のみ）
  try {
    const { purchases } = await NativePurchases.getPurchases({ onlyCurrentEntitlements: true });
    const live = (purchases || []).filter(isLiveEntitlement);
    if (live.length) return live;
  } catch (_) {
    // getPurchases 未対応のプラグインバージョン → フォールバックへ
  }

  // フォールバック: Transaction.all から失効していないものを拾う
  try {
    const { purchases } = await NativePurchases.getPurchases();
    return (purchases || []).filter(isLiveEntitlement);
  } catch (_) {
    return [];
  }
}

/**
 * iOS: 購入の復元（App Store ガイドライン 3.1.1 の「Restore」ボタンから呼ぶ）
 */
export async function restorePremiumViaIap() {
  if (!isIosNativeCheckout()) {
    throw new Error('App Store 購入の復元は iOS アプリでのみ利用できます');
  }

  const ok = await ensureStoreKitAvailable();
  if (!ok) {
    throw new Error('この端末ではアプリ内課金が利用できません');
  }

  // 1) 端末の購入履歴を App Store と同期する（必要なら Apple ID のサインインが求められる）
  try {
    await NativePurchases.restorePurchases();
  } catch (e) {
    if (isUserCancelledError(e)) {
      const err = new Error('復元がキャンセルされました');
      err.cancelled = true;
      throw err;
    }
    throw new Error(e?.message || e?.errorMessage || '購入の復元に失敗しました');
  }

  // 2) 同期後のエンタイトルメントを読み出す
  const purchases = await fetchActiveIapEntitlements();
  const match = purchases.find(
    (p) => (p.productIdentifier || p.identifier) === IAP_PREMIUM_PRODUCT_ID
  );
  if (!match) {
    const err = new Error(
      '復元できる購入が見つかりませんでした。購入時と同じ Apple ID でサインインしているかご確認ください。'
    );
    err.noPurchases = true;
    throw err;
  }

  const transactionId = match.transactionId || match.originalTransactionId || match.id;

  try {
    await syncPremiumAfterIap(transactionId, 'ios_iap_restore');
  } catch (e) {
    throw new Error(
      e?.response?.data?.error || '復元は検出しましたが、サーバーへの反映に失敗しました'
    );
  }

  return { method: 'ios_iap_restore', transactionId };
}

/** 【一時診断】依存している StoreKit API が実在し、期待の形を返すか確認する */
export async function diagnoseStoreKitApis() {
  const out = [];
  try {
    const v = await NativePurchases.getPluginVersion();
    out.push('plugin=' + JSON.stringify(v));
  } catch (e) { out.push('getPluginVersion ERR: ' + (e?.message || e)); }

  try {
    const r = await NativePurchases.getPurchases({ onlyCurrentEntitlements: true });
    out.push('getPurchases(currentEntitlements) OK: keys=' + JSON.stringify(Object.keys(r || {}))
      + ' count=' + ((r && r.purchases) ? r.purchases.length : 'undefined'));
  } catch (e) { out.push('getPurchases(currentEntitlements) ERR: ' + (e?.message || e)); }

  try {
    const r = await NativePurchases.getPurchases();
    out.push('getPurchases(all) OK: count=' + ((r && r.purchases) ? r.purchases.length : 'undefined'));
  } catch (e) { out.push('getPurchases(all) ERR: ' + (e?.message || e)); }

  return out.join(' | ');
}

/** iOS: App Store のサブスクリプション管理画面（解約・プラン変更）を開く */
export async function openIosManageSubscriptions() {
  if (!isIosNativeCheckout()) {
    throw new Error('サブスクリプションの管理は iOS アプリでのみ利用できます');
  }
  try {
    await NativePurchases.manageSubscriptions();
  } catch (e) {
    if (isUserCancelledError(e)) return;
    throw new Error(e?.message || 'サブスクリプション管理画面を開けませんでした');
  }
}

/**
 * Web / Android: Stripe Checkout を開始
 * @param {{ plan?: 'monthly'|'yearly'|'lifetime' }} [options]
 */
export async function startStripePremiumCheckout({ plan = 'monthly' } = {}) {
  const { data } = await axios.post('/api/billing/premium/checkout', { plan });
  if (!data?.url) {
    throw new Error('決済ページの作成に失敗しました');
  }
  return {
    method: 'stripe',
    url: data.url,
    sessionId: data.sessionId,
  };
}

/**
 * プレミアム購入エントリポイント
 * @param {{ refreshMe?: () => Promise<void>, plan?: 'monthly'|'yearly'|'lifetime' }} [options]
 */
export async function purchasePremium({ refreshMe, plan = 'monthly' } = {}) {
  if (isIosNativeCheckout()) {
    const result = await purchasePremiumViaIap();
    if (refreshMe) await refreshMe();
    return result;
  }

  const result = await startStripePremiumCheckout({ plan });
  const opened = openCheckoutUrl(result.url);
  if (!opened) {
    const err = new Error('決済ページを開けませんでした。リンクをタップしてください。');
    err.checkoutUrl = result.url;
    throw err;
  }
  return result;
}
